import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ModalComponent from 'components/modal/modalComponent';
import { ModalButtonProps } from 'components/modal/modal.interfaces';
import InputComponent from 'components/inputComponent';
import { CANCEL_APPOINTMENT_ID } from '../upcoming-appointments/helper/const';
import { useGetAppointments } from '../../pages/payments/hook';
import {
  GetAppointmentOutput,
  Hasura_Legal_Document_Types_Enum,
  useAcceptLegalDocumentMutation,
} from 'graphql/generated/hasura';
import { useCancelAppointmentMutation } from 'graphql/generated/remote-schema-hasura';
import { useGetComponent } from 'hooks/useGetComponent';
import { componentIds } from 'utilities/constants';
import Loader from 'components/loaderComponent';
import { twMerge } from 'tailwind-merge';
import Alert from 'components/alertComponent';
import { FHIR_APPOINTMENT_STATUS } from 'utilities/interfaces';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../../../firebase/interfaces';
import { formatDateForAppointment } from 'utilities/functions';
import { LEGAL_DOCUMENTS_ROUTE } from 'utilities/routes';
import moment from 'moment/moment';

interface ICancelAppointmentModalProps {
  callback?: () => void;
}

const CancelAppointmentModal: React.FC<ICancelAppointmentModalProps> = ({
  callback,
}) => {
  const [url, setUrl] = useSearchParams();
  const appointmentId = url.get(CANCEL_APPOINTMENT_ID);
  const { user } = useAuth();

  const isProvider = user?.role === Roles.PROVIDER;

  const { data: locale, loading: loading } = useGetComponent({
    componentId: componentIds.APPOINTMENT_PREWORK,
    locale: 'en',
  });

  const [appointment, setAppointment] = useState<GetAppointmentOutput>();
  const [errorMsg, setErrorMsg] = useState<string>('');

  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const handleCheckboxChange = (checked: boolean) => {
    setIsCheckboxChecked(checked);
  };

  const handleGoBack = useCallback(() => {
    url.delete(CANCEL_APPOINTMENT_ID);
    setIsCheckboxChecked(false);
    setErrorMsg('');
    setAppointment(undefined);
    setUrl(url);
  }, [url, setUrl]);

  const getData = useGetAppointments(appointmentId);

  const [acceptLegalDocument, { loading: loadingAccept }] =
    useAcceptLegalDocumentMutation({
      variables: {
        legal_document_type:
          Hasura_Legal_Document_Types_Enum.CancellationPolicy,
      },
      refetchQueries: ['GetMyAcceptedLegalDocuments'],
    });

  const [cancelAppointment, { loading: cancelAppointmentLoading }] =
    useCancelAppointmentMutation({
      variables: {
        codexAppointmentId: String(appointmentId),
        cancelationReason: '',
      },
      onCompleted: () => {
        setErrorMsg('');
        handleGoBack();
        if (!callback) {
          return;
        }
        callback();
      },
      onError: (error) => {
        setErrorMsg(error?.message);
      },
    });

  const handleCancel = () =>
    Promise.all([cancelAppointment(), acceptLegalDocument()]);

  const Buttons: ModalButtonProps[] = [
    {
      label: locale?.cancelModal?.cancelBtn,
      onClick: handleCancel,
      disabled: !isCheckboxChecked || cancelAppointmentLoading || loadingAccept,
      className: '!text-sm !font-bold !rounded !py-2.5 !leading-[20px] w-auto',
      testID: 'cancel-app',
    },
    {
      label: locale?.cancelModal?.backBtn,
      onClick: handleGoBack,
      type: 'underline',
      testID: 'back-btn',
      className: '!text-sm !font-bold',
    },
  ];

  useEffect(() => {
    if (appointmentId) {
      getData()
        .then((data) => {
          if (data?.status === FHIR_APPOINTMENT_STATUS.CANCELLED) {
            return handleGoBack();
          }
          setAppointment(data);
        })
        .catch((error) => console.error(error));
    }
  }, [appointmentId, getData, handleGoBack]);

  if (!appointmentId || !locale || loading) {
    return null;
  }

  return isProvider ? (
    <ModalComponent
      titleCustomClass="normal-case text-h4 desktop:!text-h3"
      titleContainerCustomClass="desktop:!min-h-[70px]"
      buttonsCustomClass="desktop:!py-[30px] max-desktop:px-7"
      buttonsContainerCustomClass="max-desktop:flex-col-reverse"
      buttons={Buttons}
      isOpen={true}
      title={'Cancel appointment'}
      contentContainerGap="gap-0"
      onClose={handleGoBack}
      modalDesktopWidth="desktop:max-w-[600px]"
      testID={'cancel-appointment-modal'}
      customContentContainerClass={'desktop:max-h-[553px] overflow-y-scroll'}
    >
      <div
        className={twMerge(
          'z-40 absolute hidden items-center justify-center w-full h-full left-0 top-0 bg-[rgba(255,255,255,.5)]',
          cancelAppointmentLoading && 'flex',
        )}
      >
        <Loader testID={'cancel-appointment-loader'} />
      </div>
      {errorMsg && <Alert type="warning" text={errorMsg} />}

      <Alert
        type="info"
        customContent={
          <p className="font-semibold text-amber-border">
            Rescheduling and cancellation are available according to our
            platform legal rules - see the{' '}
            <Link
              target="_blank"
              to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.CancellationPolicy}`}
              className={'text-clc-blue underline mx-1'}
            >
              Decode.Me Appointment Cancellation and Late Arrival Policy
            </Link>{' '}
            for details
          </p>
        }
      />

      <p className="mt-30 text-med-gray-3">
        Are you sure you want to cancel your appointment with{' '}
        <span className="text-neutral-800 font-semibold">
          {appointment?.providerFirstName} {appointment?.providerLastName}
        </span>{' '}
        scheduled for{' '}
        <span className="text-neutral-800 font-semibold">
          {formatDateForAppointment(
            moment(appointment?.start || undefined).toDate(),
            {
              withTimeZone: false,
            },
          )}
        </span>{' '}
        ?
      </p>

      <div className="flex flex-row gap-2.5 items-start mt-8">
        <InputComponent
          testID="policy-input"
          type="checkbox"
          name="agreementsCheckbox"
          checkboxProps={{
            checked: isCheckboxChecked,
            onCheckboxChange: handleCheckboxChange,
            isAlternativeCheckboxColor: true,
            checkboxLabel: (
              <div className="text-med-gray text-sm font-semibold">
                You are confirming that you have read, understood, and agree to
                our{' '}
                <Link
                  target="_blank"
                  to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.CancellationPolicy}`}
                  className={'text-clc-blue underline mx-1'}
                >
                  Appointment Cancellation Policy.
                </Link>
                <span className="text-alert-negative">*</span>
              </div>
            ),
          }}
        />
      </div>
    </ModalComponent>
  ) : (
    <ModalComponent
      titleCustomClass="normal-case text-h4 desktop:!text-h3"
      titleContainerCustomClass="desktop:!min-h-[70px]"
      buttonsCustomClass="desktop:!py-[30px] max-desktop:px-7"
      buttonsContainerCustomClass="max-desktop:flex-col-reverse"
      buttons={Buttons}
      isOpen={true}
      title={'Cancel appointment'}
      contentContainerGap="gap-0"
      onClose={handleGoBack}
      modalDesktopWidth="desktop:max-w-[600px]"
      testID={'cancel-appointment-modal'}
    >
      <div
        className={twMerge(
          'z-40 absolute hidden items-center justify-center w-full h-full left-0 top-0 bg-[rgba(255,255,255,.5)]',
          cancelAppointmentLoading && 'flex',
        )}
      >
        <Loader testID={'cancel-appointment-loader'} />
      </div>
      {errorMsg && <Alert type="warning" text={errorMsg} />}

      <Alert
        type="info"
        customContent={
          <p className="font-semibold text-amber-border">
            Rescheduling and cancellation are available according to our
            platform legal rules - see the{' '}
            <Link
              target="_blank"
              to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.CancellationPolicy}`}
              className={'text-clc-blue underline mx-1'}
            >
              Decode.Me Appointment Cancellation and Late Arrival Policy
            </Link>{' '}
            for details
          </p>
        }
      />

      <p className="mt-30 text-med-gray-3">
        Are you sure you want to cancel your appointment with{' '}
        <span className="text-neutral-800 font-semibold">
          {appointment?.providerFirstName} {appointment?.providerLastName}
        </span>{' '}
        scheduled for{' '}
        <span className="text-neutral-800 font-semibold">
          {formatDateForAppointment(
            moment(appointment?.start || undefined).toDate(),
            {
              withTimeZone: false,
            },
          )}
        </span>{' '}
        ?
      </p>

      <div className="flex flex-row gap-2.5 items-start mt-8">
        <InputComponent
          testID="policy-input"
          type="checkbox"
          name="agreementsCheckbox"
          checkboxProps={{
            checked: isCheckboxChecked,
            onCheckboxChange: handleCheckboxChange,
            isAlternativeCheckboxColor: true,
            checkboxLabel: (
              <div className="text-med-gray text-sm font-semibold">
                You are confirming that you have read, understood, and agree to
                our{' '}
                <Link
                  target="_blank"
                  to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.CancellationPolicy}`}
                  className={'text-clc-blue underline mx-1'}
                >
                  Appointment Cancellation Policy.
                </Link>
                <span className="text-alert-negative">*</span>
              </div>
            ),
          }}
        />
      </div>
    </ModalComponent>
  );
};

export default CancelAppointmentModal;
